import { z } from 'zod';

const numLike = z.union([z.number(), z.string(), z.null()]).transform((v) => {
  if (v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
});

export const RestaurantSummary = z.object({
  permit_number: z.string(),
  permit_id: z.string().optional(),
  restaurant_name: z.string(),
  address: z.string(),
  city_name: z.string(),
  state: z.string(),
  zip_code: z.string(),
  latitude: numLike,
  longitude: numLike,
  current_grade: z.string(),
  current_demerits: z.coerce.number().nullable(),
  date_current: z.string().nullable(),
  category_name: z.string(),
  inspection_type: z.string().nullable(),
});
export type RestaurantSummary = z.infer<typeof RestaurantSummary>;

export const SearchResponse = z.object({
  total: z.number(),
  page: z.number(),
  per_page: z.number(),
  used_fallback_search: z.boolean().optional(),
  results: z.array(RestaurantSummary),
});
export type SearchResponse = z.infer<typeof SearchResponse>;

export const Violation = z.object({
  id: z.coerce.number(),
  demerits: z.coerce.number(),
  description: z.string(),
});
export type Violation = z.infer<typeof Violation>;

export const PreviousInspection = z.object({
  inspection_date: z.string().nullable(),
  inspection_time: z.string().nullable(),
  inspection_type: z.string().nullable(),
  inspection_grade: z.string().nullable(),
  inspection_demerits: z.coerce.number().nullable(),
  violations: z.string().nullable(),
  violations_resolved: z.array(Violation).default([]),
});
export type PreviousInspection = z.infer<typeof PreviousInspection>;

export const RestaurantDetail = RestaurantSummary.extend({
  facility_id: z.string().optional(),
  current_violations: z.string().nullable(),
  current_violations_resolved: z.array(Violation).default([]),
  previous_inspections: z.array(PreviousInspection),
});
export type RestaurantDetail = z.infer<typeof RestaurantDetail>;

export const CaseOverrideSchema = z.object({
  token: z.string(),
  display: z.string(),
  scope: z.enum(['both', 'name', 'address']),
});
export type CaseOverride = z.infer<typeof CaseOverrideSchema>;

export const Lookups = z.object({
  categories: z.array(z.object({ id: z.number(), name: z.string() })),
  cities: z.array(z.object({ id: z.number(), name: z.string() })),
  grades: z.array(z.object({ code: z.string(), label: z.string() })),
  overrides: z.array(CaseOverrideSchema).default([]),
});
export type Lookups = z.infer<typeof Lookups>;
